import React, { useRef, useEffect, useState } from "react";
import { Box, Grid, Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PrimaryImageUploader from "./PrimaryImageUploader";
import ReferenceImageUploader from "./ReferenceImageUploader";
import CustomizationFields from "./CustomizationFields";
import ItemDetailsForm from "./ItemDetailsForm";
import {
  uploadReferenceImage,
  uploadPrimaryImage,
} from "../../../utils/uploadToS3";
import api from "../../../services/api";

// Utility: Calculate diet extra
const getDietExtra = (diet, size) => {
  if (!diet || !size?.sizeId) return 0;
  return (
    diet.priceAdjustments?.find((adj) => adj.sizeId === size.sizeId)?.amount ||
    0
  );
};

// Utility: Calculate total graze box price
const calculateGrazeBoxPrice = (size, diet, giftPackaging) => {
  if (!size) return 0;
  const base = size.basePrice || 0;
  const dietAdj = getDietExtra(diet, size);
  const gift = giftPackaging ? 3 : 0;
  return base + dietAdj + gift;
};

const AdminOrderItemCard = ({ item, meta, index, updateItem, removeItem }) => {
  const primaryInputRef = useRef();
  const secondaryInputRef = useRef();

  const [flavors, setFlavors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [standardCakes, setStandardCakes] = useState([]);
  const [cheesecakes, setCheesecakes] = useState([]);
  const [grazeBoxes, setGrazeBoxes] = useState([]);
  const [patisserieItems, setPatisserieItems] = useState([]);
  const [isStandardDesign, setIsStandardDesign] = useState(true);
  const [rawCents, setRawCents] = useState(
    String(Math.round((item.unitPrice || 0) * 100))
  );

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [
          flavorsRes,
          sizesRes,
          stdCakeRes,
          cheesecakeRes,
          patisserieRes,
          grazeBoxRes,
        ] = await Promise.all([
          api.get("/flavors"),
          api.get("/sizes"),
          api.get("/standard-cakes"),
          api.get("/cheesecakes"),
          api.get("/patisserie"),
          api.get("/graze-boxes"),
        ]);

        setFlavors(flavorsRes.data || []);
        setSizes(sizesRes.data || []);
        setStandardCakes(stdCakeRes.data || []);
        setCheesecakes(cheesecakeRes.data || []);
        setPatisserieItems(patisserieRes.data || []);
        setGrazeBoxes(grazeBoxRes.data || []);
      } catch (err) {
        console.error("Failed to fetch item form options:", err);
      }
    };

    fetchAll();
  }, []);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    updateItem(item._id, { [field]: value });
  };

  const handleCustomizationChange = (i, keyOrValue, value) => {
    const customizations = [...item.customizations];
    customizations[i][keyOrValue] = value;
    updateItem(item._id, { customizations });
  };

  const addCustomization = () => {
    const customizations = [
      ...(item.customizations || []),
      { key: "", value: "", label: "" },
    ];
    updateItem(item._id, { customizations });
  };

  const removeCustomization = (i) => {
    const customizations = item.customizations.filter((_, idx) => idx !== i);
    updateItem(item._id, { customizations });
  };

  const handlePrimaryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const [file] = files;
    const uploaded = await uploadPrimaryImage(
      file,
      "main_style",
      meta.uploadSessionId
    );

    if (uploaded?.url) {
      updateItem(item._id, { image: uploaded.url });
    }

    e.target.value = null;
  };

  const handleReferenceUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const uploads = [];

    for (const file of files) {
      try {
        const uploaded = await uploadReferenceImage(file);
        if (uploaded?.url) {
          uploads.push({
            url: uploaded.url,
            purpose: "", // default blank purpose
            customPurpose: "",
            fileName: file.name,
          });
        }
      } catch (err) {
        console.error("Reference image upload failed:", err);
      }
    }

    const updated = [...(item.referenceImages || []), ...uploads];
    updateItem(item._id, { referenceImages: updated });

    e.target.value = null;
  };

  const handlePurposeChange = (imgIndex, newPurpose, customPurpose = "") => {
    const updated = [...(item.referenceImages || [])];
    updated[imgIndex] = {
      ...updated[imgIndex],
      purpose: newPurpose,
      customPurpose: newPurpose === "other" ? customPurpose : "",
    };
    updateItem(item._id, { referenceImages: updated });
  };

  const handleImageRemove = (imgIndex) => {
    const updated = [...(item.referenceImages || [])].filter(
      (_, i) => i !== imgIndex
    );
    updateItem(item._id, { referenceImages: updated });
  };

  return (
    <Box border={1} borderColor="grey.300" borderRadius={2} p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}>
        <Typography fontWeight="bold">Item {index + 1}</Typography>
        <IconButton onClick={() => removeItem(item._id)} size="small">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <ItemDetailsForm
          item={item}
          updateItem={updateItem}
          flavors={flavors}
          sizes={sizes}
          standardCakes={standardCakes}
          cheesecakes={cheesecakes}
          grazeBoxes={grazeBoxes}
          patisserieItems={patisserieItems}
          isStandardDesign={isStandardDesign}
          setIsStandardDesign={setIsStandardDesign}
          rawCents={rawCents}
          setRawCents={setRawCents}
          getDietExtra={getDietExtra}
          calculateGrazeBoxPrice={calculateGrazeBoxPrice}
        />

        <PrimaryImageUploader
          image={item.image}
          onUpload={handlePrimaryUpload}
          onRemove={() => updateItem(item._id, { image: "" })}
          fileInputRef={primaryInputRef}
        />

        <ReferenceImageUploader
          images={item.referenceImages || []}
          onUpload={handleReferenceUpload}
          onRemove={handleImageRemove}
          onPurposeChange={handlePurposeChange}
          fileInputRef={secondaryInputRef}
        />

        <Grid item xs={12}>
          <CustomizationFields
            customizations={item.customizations}
            onChange={handleCustomizationChange}
            onAdd={addCustomization}
            onRemove={removeCustomization}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" fontWeight={500}>
            Total: ${item.totalPrice.toFixed(2)}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminOrderItemCard;
